import { useEffect, useState } from 'react'
import { apiQueryPersister } from 'features/api/apiQueryClient'
import { GRAPH_SAMPLE_INTERVAL_MS } from './performanceMonitorBar.constants'
import { QueryCacheEntry } from './performanceMonitorBar.types'

type PersistedCacheMeasurement = {
  entries: QueryCacheEntry[]
  totalBytes: number
}

const EMPTY_MEASUREMENT: PersistedCacheMeasurement = {
  entries: [],
  totalBytes: 0
}

const measurePersistedCache = async (): Promise<PersistedCacheMeasurement> => {
  const client = await apiQueryPersister.restoreClient()
  if (!client) return EMPTY_MEASUREMENT

  const entries = client.clientState.queries.reduce<QueryCacheEntry[]>(
    (acc, query) => {
      if (query.state.data === undefined) return acc
      try {
        const bytes = new Blob([JSON.stringify(query.state.data)]).size
        return [
          ...acc,
          {
            key: JSON.stringify(query.queryKey),
            bytes,
            dataUpdatedAt: query.state.dataUpdatedAt
          }
        ]
      } catch {
        return acc
      }
    },
    []
  )

  const totalBytes = entries.reduce((acc, entry) => acc + entry.bytes, 0)
  return { entries, totalBytes }
}

export const usePersistedCacheUsage = () => {
  const [measurement, setMeasurement] =
    useState<PersistedCacheMeasurement>(EMPTY_MEASUREMENT)

  useEffect(() => {
    let cancelled = false

    const refresh = async () => {
      const next = await measurePersistedCache()
      if (!cancelled) setMeasurement(next)
    }

    refresh()
    const interval = setInterval(refresh, GRAPH_SAMPLE_INTERVAL_MS)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [])

  return {
    entries: measurement.entries,
    totalEntries: measurement.entries.length,
    totalBytes: measurement.totalBytes
  }
}
